/**
 * First-touch acquisition capture for CRM attribution.
 *
 * UTM params and `ref` arrive on the URL the user lands with and disappear on
 * the first in-app navigation. We capture them once per browser session
 * (first touch wins), sanitize URL values BEFORE storage, and persist them in
 * sessionStorage. The stored object is later written to Clerk
 * `user.unsafeMetadata.acquisition` (see use-acquisition-tracking.ts), which
 * the CRM admin reads when the user's organization is created.
 *
 * The payload is capped to the attribution keys below (UTMs, `ref`, ad
 * click ids, referrer, landing page) — no PII, no cookies, no timestamps.
 * Click ids matter because auto-tagging is the Google Ads default: a paid
 * Google click carries a bare `gclid` with zero `utm_*` (TAG-2514). The list
 * mirrors admin-app's `ACQUISITION_CLICK_ID_KEYS` — the CRM admin classifies
 * the lead as paid from them. URL sanitization mirrors the admin's D6 rules:
 * keep host + path, allow only `utm_*` / `ref` query params, cap at 2000 chars.
 */

export interface AcquisitionData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  ref?: string;
  /** Google Ads (gbraid/wbraid = iOS/app-campaign variants). */
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  /** Meta / TikTok / Microsoft / X Ads click ids. */
  fbclid?: string;
  ttclid?: string;
  msclkid?: string;
  twclid?: string;
  referrer?: string;
  landingPage?: string;
}

const STORAGE_KEY = 'crm_acquisition_data';

const CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid', 'twclid'] as const;

const TRACKING_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'ref',
  ...CLICK_ID_PARAMS,
] as const;

const ACQUISITION_KEYS = [...TRACKING_PARAMS, 'referrer', 'landingPage'] as const;

/** Query params allowed to survive on stored `referrer` / `landingPage` URLs. */
const URL_QUERY_ALLOWLIST = /^(utm_[a-z]+|ref)$/;
const URL_MAX_LENGTH = 2000;
const PARAM_MAX_LENGTH = 200;
/** Click ids are opaque tokens (~40-120 chars) — cap higher than UTM values. */
const CLICK_ID_MAX_LENGTH = 512;

/**
 * Keep host + path (+ allowlisted `utm_*` / `ref` query params) of a URL and
 * strip everything else (fragments, credentials, tracking junk). Values that
 * don't parse as URLs are kept verbatim (a bare hostname is still signal),
 * trimmed and capped at 2000 chars. Empty/blank input returns undefined.
 */
export function sanitizeAcquisitionUrl(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return trimmed.slice(0, URL_MAX_LENGTH);
  }

  const params = new URLSearchParams();
  for (const [key, value] of url.searchParams) {
    if (URL_QUERY_ALLOWLIST.test(key.toLowerCase())) params.append(key, value);
  }
  const query = params.toString();
  const out = `${url.protocol}//${url.host}${url.pathname}${query ? `?${query}` : ''}`;
  return out.slice(0, URL_MAX_LENGTH);
}

/**
 * Pure extraction from raw location values (kept parameterized so it stays
 * testable in the node vitest environment). Only sets keys that carry a
 * value, so the result JSON-serializes without undefined noise.
 */
export function extractAcquisition(
  search: string,
  referrer: string,
  href: string,
): AcquisitionData {
  const searchParams = new URLSearchParams(search);
  const out: AcquisitionData = {};
  for (const param of TRACKING_PARAMS) {
    const value = searchParams.get(param)?.trim();
    if (!value) continue;
    const cap = (CLICK_ID_PARAMS as readonly string[]).includes(param)
      ? CLICK_ID_MAX_LENGTH
      : PARAM_MAX_LENGTH;
    out[param] = value.slice(0, cap);
  }
  const cleanReferrer = sanitizeAcquisitionUrl(referrer);
  if (cleanReferrer) out.referrer = cleanReferrer;
  const cleanLandingPage = sanitizeAcquisitionUrl(href);
  if (cleanLandingPage) out.landingPage = cleanLandingPage;
  return out;
}

/** True when any UTM param, `ref` or ad click id is present. */
export function hasTrackingParams(data: AcquisitionData): boolean {
  return TRACKING_PARAMS.some((param) => Boolean(data[param]));
}

/** True when the object carries at least one acquisition key. */
export function hasAcquisitionData(data: AcquisitionData | null | undefined): data is AcquisitionData {
  return Boolean(data && Object.keys(data).length > 0);
}

/**
 * First-touch merge: the session's first capture wins. A later URL may only
 * contribute UTM/ref params when the first touch had none (organic entry
 * followed by a tagged re-entry); `referrer` and `landingPage` always keep
 * their original first-touch values.
 */
export function mergeFirstTouch(
  stored: AcquisitionData | null,
  fresh: AcquisitionData,
): AcquisitionData | null {
  if (!hasAcquisitionData(stored)) {
    return hasAcquisitionData(fresh) ? fresh : null;
  }
  if (!hasTrackingParams(stored) && hasTrackingParams(fresh)) {
    return {
      ...fresh,
      referrer: stored.referrer ?? fresh.referrer,
      landingPage: stored.landingPage ?? fresh.landingPage,
    };
  }
  return stored;
}

/** Whitelist-copy: guarantees the stored object never exceeds the known keys. */
function pickAcquisitionKeys(data: Record<string, unknown>): AcquisitionData {
  const out: AcquisitionData = {};
  for (const key of ACQUISITION_KEYS) {
    const value = data[key];
    if (typeof value === 'string' && value) out[key] = value;
  }
  return out;
}

/** Read the first-touch acquisition stored for this browser session. */
export function getStoredAcquisition(): AcquisitionData | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const picked = pickAcquisitionKeys(parsed as Record<string, unknown>);
    return hasAcquisitionData(picked) ? picked : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Cross-domain linker mark (`?tgd_tm=`)
// ---------------------------------------------------------------------------

/**
 * Decorated links from the tagada.io landings carry `?tgd_tm=<thumbmark>` —
 * the ThumbmarkJS mark of the device that clicked through. It may differ
 * from THIS browser's own thumbmark (in-app browser hops), so it is stored
 * separately and written to `unsafeMetadata.device_thumbmark_linked`, giving
 * the CRM's ad-touch stitcher a deterministic join back to the landing touch.
 */
const LINKED_TM_STORAGE_KEY = 'crm_linked_thumbmark';
const LINKED_TM_PARAM = 'tgd_tm';
const THUMBMARK_RE = /^[a-f0-9]{8,128}$/;

/** Persist the linker mark from the current URL (first value wins). */
export function persistLinkedThumbmarkFromUrl(): void {
  if (typeof window === 'undefined') return;
  try {
    if (sessionStorage.getItem(LINKED_TM_STORAGE_KEY)) return;
    const raw = new URLSearchParams(window.location.search).get(LINKED_TM_PARAM);
    const value = raw?.trim().toLowerCase();
    if (!value || !THUMBMARK_RE.test(value)) return;
    sessionStorage.setItem(LINKED_TM_STORAGE_KEY, value);
  } catch {
    // Private browsing / quota — best-effort only.
  }
}

/** Read the linker mark captured for this browser session. */
export function getStoredLinkedThumbmark(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = sessionStorage.getItem(LINKED_TM_STORAGE_KEY);
    return value && THUMBMARK_RE.test(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * Persist first-touch acquisition from the current URL. Called as early as
 * possible on app load (main.tsx) — before any routing can strip the landing
 * URL's query params — and again from useAcquisitionTracking as a backstop.
 */
export function persistAcquisitionFromUrl(): void {
  if (typeof window === 'undefined') return;
  const fresh = extractAcquisition(
    window.location.search,
    typeof document !== 'undefined' ? document.referrer : '',
    window.location.href,
  );
  const next = mergeFirstTouch(getStoredAcquisition(), fresh);
  if (!next) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private browsing / quota — best-effort only.
  }
}
